import { Context } from '../context/Context';
import { BufferTexture } from '../texture';
import { Material } from '../material';
import { ResolvedMaterialTextures } from './GltfParser';

/**
 * Upload a glTF parser's resolved material textures to the GPU and bundle
 * each material's textures into a {@link Material} keyed by material index.
 *
 * Pure parsing → texture pipeline. Object construction lives in
 * {@link RenderObjectFactory.instantiateResolvedMesh}.
 *
 * Per-material pattern: decode all images in parallel (CPU), then run the GL
 * upload block synchronously. Matches `feedback_async_gl_operations` — no GL
 * calls span an `await`. Materials are processed sequentially to bound peak
 * memory pressure during scene load.
 */
export async function resolveMaterialTextures(
  ctx: Context,
  resolvedTextures: ResolvedMaterialTextures[],
): Promise<Map<number, Material>> {
  const materials = new Map<number, Material>();
  for (const rt of resolvedTextures) {
    const [baseImg, normalImg, metRoughImg] = await Promise.all([
      rt.baseColor
        ? ctx.resourceLoader.loadImageData(rt.baseColor.imageData, rt.baseColor.mimeType)
        : Promise.resolve(null),
      rt.normalMap
        ? ctx.resourceLoader.loadImageData(rt.normalMap.imageData, rt.normalMap.mimeType)
        : Promise.resolve(null),
      rt.metallicRoughness
        ? ctx.resourceLoader.loadImageData(
            rt.metallicRoughness.imageData,
            rt.metallicRoughness.mimeType,
          )
        : Promise.resolve(null),
    ]);

    const mat = new Material();
    if (baseImg) {
      const texture = new BufferTexture(baseImg).initialize(ctx);
      if (rt.alphaMode === 'BLEND') texture.translucent = true;
      mat.baseColor = texture;
    }
    if (normalImg) {
      mat.normalMap = new BufferTexture(normalImg).initialize(ctx, false);
    }
    if (metRoughImg) {
      mat.metallicRoughnessMap = new BufferTexture(metRoughImg).initialize(ctx, false);
    }
    materials.set(rt.materialIndex, mat);
  }
  return materials;
}
